import React, { useEffect, useMemo, useState } from 'react'
import { Card } from 'primereact/card'
import { Icon } from '@iconify/react';
import { useAuth } from '../contexts/AuthContext';
import { useCombo } from '../contexts/CombosContext';
import { ComboNav } from '../components/NavigationPath'
import { useCompanySetting } from '../contexts/CompanySettingContext';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2'
import AddCombo from '../components/AddCombo';
import PageHeader from '../components/PageHeader';
import InfiniteScroll from 'react-infinite-scroll-component';

function ComboList() {
    const { getAllCombos, getSingleCombo } = useCombo();
    const { currency } = useCompanySetting()
    const { restaurantId, auth, hasPermission } = useAuth();

    const canCreate = useMemo(() => hasPermission("products", "create"),  [hasPermission]);
    const canUpdate = useMemo(() => hasPermission("products", "update"), [hasPermission]);
    const canDelete = useMemo(() => hasPermission("products", "delete"), [hasPermission]);    

    const [comboVisible, setComboVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editComboId, setEditComboId] = useState(null);
    const [comboList, setComboList] = useState([])
    const [isLoading, setIsLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalRenderedRecords, setTotalRenderedRecords] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [comboDetails, setComboDetails] = useState({
        name: '',
        plu: '',
        category_id: '',
        price: '',
        choices: [],
        image: '',
        visible: 0,
        finalTotalPrice: 0,
        restaurant_id: restaurantId
    });
    const [filter, setFilter] = useState({
        limit: 50,
        restaurant_id: restaurantId,
        totalRenderedRecords: 0,
    })

    useEffect(() => {
        if (restaurantId) {
            handleFilterChange({ ...filter, restaurant_id: restaurantId })
        }
    }, [restaurantId])

    useEffect(() => {
        if (filter) {
            fetchCombos(filter)
        }
    }, [filter])

    const fetchCombos = async (filter) => {
        try {
            if (restaurantId) {
                setIsLoading(true)

                const data = await getAllCombos(filter)
                if (data.combos.length !== 0 && data.totalCount !== comboList.length) {
                    setComboList([...comboList, ...data.combos])
                    setTotalRecords(data.totalCount)
                    setTotalRenderedRecords(comboList.length)
                }

                setIsLoading(false)
            }
        } catch (error) {
            console.error('Error fetching combos', error);
        }
    }

    const handleCardClick = async (comboId) => {
        if(!canUpdate && !canDelete) return;

        setEditComboId(comboId);
        setEditMode(true);
        setComboVisible(false);
    }

    const handleFilterChange = async (newFilter) => {
        setComboList([]);
        setFilter({ ...newFilter, totalRenderedRecords: 0 });
        setHasMore(true)
    };

    const handleLoadMore = () => {
        if (comboList.length === totalRecords) {
            setHasMore(false);
        } else if (comboList.length === filter.limit) {
            const updatedFilter = { ...filter, totalRenderedRecords: filter.limit };

            setFilter(updatedFilter);
            setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
        } else {
            const updatedFilter = { ...filter, totalRenderedRecords: totalRenderedRecords + filter.limit };

            setFilter(updatedFilter);
            setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
        }
    };

    const fetchComboProps = async (newComboId) => {
        try {
            if (editMode && editComboId) {
                const combo = await getSingleCombo(editComboId);
                const comboIndex = comboList.findIndex((c) => c.id === editComboId);

                if (comboIndex !== -1) {
                    if (!combo) {
                        const updatedComboList = [...comboList];
                        updatedComboList.splice(comboIndex, 1);

                        setComboList(updatedComboList);
                        setTotalRecords(totalRecords - 1);
                        setTotalRenderedRecords(totalRenderedRecords - 1)
                    } else {
                        const updatedComboList = [...comboList];
                        updatedComboList[comboIndex] = combo;

                        setComboList(updatedComboList);
                    }
                } else {
                    setComboList([...comboList, combo]);
                }
            } else if (comboVisible) {
                const updatedComboList = [...comboList];

                updatedComboList.unshift({ ...comboDetails, id: newComboId });

                setComboList(updatedComboList);
                setTotalRecords(totalRecords + 1);
                setTotalRenderedRecords(totalRenderedRecords + 1)
            }
        } catch (error) {
            console.error('Error fetching combos.', error);
        }
    };

    const renderCardsInRows = () => {
        const rows = [];
        let currentRow = [];
        let count = 0;

        comboList.length > 0 && comboList.forEach((combo, index) => {
            currentRow.push(
                <div key={combo.id} className="d-flex">
                    <Card
                        header={combo.image ? <LazyLoadImage src={combo.image} alt={combo.name} className='product-rounded-img' /> : <Icon icon="mdi:food-outline" height={125} width={70} style={{ color: "black" }} />}
                        title={
                            <>
                                <div className='product-name' title={combo.name}>{combo.name}</div>
                                <div className='product-price'>{currency}{combo.price}</div>
                            </>
                        }
                        className="product-card"
                        onClick={() => handleCardClick(combo.id)}
                    />
                </div>
            );

            count++;
            rows.push(
                <div key={index} className="d-flex">
                    {currentRow}
                </div>
            );
            currentRow = [];
            count = 0;
        });

        return rows;
    };

    return (
        <Layout2 items={ComboNav.items} navTitle={ComboNav.header}>
            <div className="d-flex flex-column w-100">
                <PageHeader title={'Combos'} />
                <div className='px-4 py-2'>
                    {(comboVisible || (editMode && editComboId)) && (
                        <AddCombo
                            editMode={editMode}
                            editComboId={editComboId}
                            comboVisible={comboVisible}
                            comboDetails={comboDetails}
                            setEditComboId={setEditComboId}
                            setEditMode={setEditMode}
                            setComboVisible={setComboVisible}
                            fetchCombos={fetchComboProps}
                            setComboDetails={setComboDetails}
                            canCreate={canCreate}
                            canUpdate={canUpdate}
                            canDelete={canDelete}
                        />
                    )}
                    <div className='d-flex flex-wrap'>
                        <InfiniteScroll
                            dataLength={comboList.length}
                            next={handleLoadMore}
                            hasMore={hasMore}
                            className="card-container"
                        >
                            {canCreate && 
                                <Card
                                    title={"Add Combo"}
                                    header={<Icon icon="tabler:plus" style={{ color: "#888888" }} height={70} width={70} />}
                                    className="add-product-card"
                                    onClick={() => setComboVisible(true)}
                                />
                            }
                            {isLoading ? (
                                <Loader />
                            ) :
                                (
                                    <>
                                        {comboList && comboList.length !== 0 &&
                                            (
                                                renderCardsInRows()
                                            )}
                                    </>
                                )
                            }
                        </InfiniteScroll>
                    </div>
                </div>
            </div>
        </Layout2 >
    )
}

export default ComboList